import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { performPutRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	ApplicationLinkCreationEntity,
	ApplicationLinkCreationResponse,
	ApplicationLinkManifest,
	ApplicationLinkOAuth2ConfigInfo,
	ApplicationLinkRemoteOAuth2ApplicationLink,
	ApplicationLinkV3Data,
	ApplicationLinkV3Simple,
	ApplicationLinkV3Status,
	RestApplinkOAuthStatus,
} from './CommonApplicationLinkCreationEntity.tsx';
import {
	APPLINKS_V3_CLOUD_CONNECTOR_QUERY_SINGULAR_URL,
	APPLINKS_V3_QUERY_SINGULAR_URL,
	APPLINKS_V3_QUERY_URL,
} from './applinksListPageUrl.constants.tsx';

// ApplicationLinkResource
export const fetchApplinks = (): Promise<ApplicationLinkV3Data[]> =>
	fetchJson(APPLINKS_V3_QUERY_URL);

export const fetchApplink = (id: string): Promise<ApplicationLinkV3Data> =>
	fetchJson(
		fg('cloud-connector-applinks-migration-m1-jira-srs')
			? APPLINKS_V3_CLOUD_CONNECTOR_QUERY_SINGULAR_URL(id)
			: APPLINKS_V3_QUERY_SINGULAR_URL(id),
	);

const putApplinkProvider = (
	id: string,
	provider: string,
): Promise<ApplicationLinkCreationResponse> =>
	fetchJson(`/rest/applinks/3.0/applicationlink/${id}/authentication/provider`, {
		method: 'PUT',
		body: JSON.stringify({
			config: {},
			provider,
		}),
		headers: {
			'Content-Type': 'application/json',
		},
	});

export const putApplinkOAuthOne3LOProvider = (
	id: string,
): Promise<ApplicationLinkCreationResponse> =>
	putApplinkProvider(id, 'com.atlassian.applinks.api.auth.types.OAuthAuthenticationProvider');

export const putApplinkOAuthOne2LOProvider = (
	id: string,
): Promise<ApplicationLinkCreationResponse> =>
	putApplinkProvider(
		id,
		'com.atlassian.applinks.api.auth.types.TwoLeggedOAuthAuthenticationProvider',
	);

// ApplinksStatusResource
export const fetchApplinkStatus = (id: string): Promise<ApplicationLinkV3Status> =>
	fetchJson(`/rest/applinks/3.0/status/${id}`);

export const putApplinkOAuth1Config = (
	id: string,
	oauthStatus: RestApplinkOAuthStatus, // oxlint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> =>
	performPutRequest(`/rest/applinks/3.0/status/${id}/oauth`, {
		body: JSON.stringify(oauthStatus),
		headers: {
			'Content-Type': 'application/json',
		},
	});

// CreateApplicationLinkUIResource
export const fetchApplinkManifestForUrl = (url: string): Promise<ApplicationLinkManifest> =>
	fetchJson(`/rest/applinks/3.0/applicationlinkForm/manifest.json?url=${encodeURIComponent(url)}`);

// ManifestResource
export const fetchThisManifest = (): Promise<ApplicationLinkManifest> =>
	fetchJson('/rest/applinks/3.0/manifest.json');

// v2/ApplicationLinkResource
export const putApplink = (
	entity: ApplicationLinkCreationEntity,
): Promise<ApplicationLinkCreationResponse> =>
	fetchJson('/rest/applinks/3.0/applicationlink', {
		method: 'PUT',
		body: JSON.stringify(entity),
		headers: {
			'Content-Type': 'application/json',
		},
	});

export const deleteApplink = (id: string): Promise<void> =>
	fetchJson(`/rest/applinks/3.0/applicationlink/${id}`, {
		method: 'DELETE',
		headers: {
			'Content-Type': 'application/json',
		},
	});

// ApplicationLinkV3Resource
export const createOAuth2Applink = (
	entity: ApplicationLinkCreationEntity,
): Promise<ApplicationLinkCreationResponse> =>
	fetchJson('/rest/applinks/3.0/applinks', {
		method: 'POST',
		body: JSON.stringify(entity),
		headers: {
			'Content-Type': 'application/json',
		},
	});

export const updateApplinkV3 = (id: string, entity: ApplicationLinkV3Simple): Promise<void> =>
	performPutRequest(`/rest/applinks/3.0/applinks/${id}`, {
		body: JSON.stringify(entity),
		headers: {
			'Content-Type': 'application/json',
		},
	});

export const getOAuth2ConfigInfo = (id: string): Promise<ApplicationLinkOAuth2ConfigInfo> =>
	fetchJson(`/rest/applinks/3.0/applinks/${id}/oAuth2ConfigInfo`, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
		},
	});

export const putOAuth2ConfigInfo = (
	id: string,
	config: ApplicationLinkRemoteOAuth2ApplicationLink,
): Promise<void> =>
	performPutRequest(`/rest/applinks/3.0/applinks/${id}/oAuth2ConfigInfo`, {
		body: JSON.stringify(config),
		headers: {
			'Content-Type': 'application/json',
		},
	});

// OAuthApplicationLinkResource
export const autoConfigureApplinkConsumer = (
	id: string,
): Promise<ApplicationLinkCreationResponse> =>
	fetchJson(
		`/rest/applinks-oauth/1.0/applicationlink/${id}/authentication/consumer?autoConfigure=true`,
		{
			method: 'PUT',
			body: JSON.stringify({
				twoLOAllowed: true,
				executingTwoLOUser: null,
				twoLOImpersonationAllowed: false,
			}),
			headers: {
				'Content-Type': 'application/json',
			},
		},
	);
